import { ContextMarshaller } from '@cgs/shared';
import { SpinResponse } from '@cgs/data-access';
import { Container, StateMachine } from '@cgs/wge';
import { T_ISpinParams } from '../../type_definitions';
import { ISpinParams } from '../game_components/i_spin_params';
import { BonusGameEvent } from './events/bonus_game_event';
import { BuyFreeSpinsEvent } from './events/BuyFreeSpinsEvent';
import { ResumeEvent } from './events/resume_event';
import { SpinEvent } from './events/spin_event';
import { StopEvent } from './events/stop_event';
import { ResponseHolder } from './response_holder';
import { BaseGameRule } from './rules/infra/base_game_rule';
import { BaseGameState } from './states/base_game_state';
import { RequestState } from './states/request_state';
import { SuspendState } from './states/suspend_state';
import { GameStateMachineStates } from './game-state-machine-states';
import { SpinRequestState } from './states/spin-request.state';
import { SlotMachineStoreState } from './states/slot-machine-store.state';

export type RuleFactory<T extends SpinResponse> = (
  from: BaseGameState<T>,
  r: ResponseHolder<T>
) => BaseGameRule<T>;

export const GAME_STATE_MACHINE_TOKEN = Symbol('GAME_STATE_MACHINE_TOKEN');

export abstract class GameStateMachine<TResponse extends SpinResponse> {
  public endAnimation: BaseGameState<TResponse>;
  public init: BaseGameState<TResponse>;
  public bonusRecovery: BaseGameState<TResponse>;
  public freeSpinsRecovery: BaseGameState<TResponse>;
  public freeSpinsCompletion: BaseGameState<TResponse>;
  public idle: BaseGameState<TResponse>;
  public spinsRequest: BaseGameState<TResponse>;
  public buyFreeSpinsRequest: BaseGameState<TResponse>;
  public accelerate: BaseGameState<TResponse>;
  public waitRequest: BaseGameState<TResponse>;
  public waitaccelerationComplete: BaseGameState<TResponse>;
  public makeRequest: BaseGameState<TResponse>;
  public stop: BaseGameState<TResponse>;
  public animation: BaseGameState<TResponse>;
  public shortWinLines: BaseGameState<TResponse>;
  public stopping: BaseGameState<TResponse>;
  public immediatelyStop: BaseGameState<TResponse>;
  public beginFreeSpins: BaseGameState<TResponse>;
  public beginFreeSpinsPopup: BaseGameState<TResponse>;
  public endFreeSpins: BaseGameState<TResponse>;
  public endFreeSpinsPopup: BaseGameState<TResponse>;
  public reBuyFreeSpinsPopup: BaseGameState<TResponse>;
  public beginBonus: BaseGameState<TResponse>;
  public bonus: BaseGameState<TResponse>;
  public endBonus: BaseGameState<TResponse>;
  public endScatter: BaseGameState<TResponse>;
  public regularSpins: BaseGameState<TResponse>;
  public autoSpins: BaseGameState<TResponse>;
  public beginRespin: BaseGameState<TResponse>;
  public respin: BaseGameState<TResponse>;
  public freeSpins: BaseGameState<TResponse>;
  public beginScatter: BaseGameState<TResponse>;
  public scatter: BaseGameState<TResponse>;

  private readonly _responseHolder: ResponseHolder<TResponse> = new ResponseHolder<TResponse>();
  private readonly _stm: StateMachine = new StateMachine();
  private readonly _container: Container;
  private readonly _spinParams: ISpinParams;

  protected constructor(container: Container) {
    this._container = container;
    this._spinParams = container.resolve<ISpinParams>(T_ISpinParams);
  }

  public initialize(): this {
    this.createStates();
    this.createRules();
    return this;
  }

  public get container(): Container {
    return this._container;
  }

  public get responseHolder(): ResponseHolder<TResponse> {
    return this._responseHolder;
  }

  public get curResponse(): TResponse {
    return this._responseHolder.curResponse;
  }

  public get prevResponse(): TResponse | null {
    return this._responseHolder.preResponse;
  }

  public get rootState(): StateMachine {
    return this._stm;
  }

  protected createStates(): void {
    this.init = this.createBaseState(GameStateMachineStates.InitGame);
    this.spinsRequest = this.createSpinState(GameStateMachineStates.SpinRequest);
    this.buyFreeSpinsRequest = this.createSlotMachineStoreState(
      GameStateMachineStates.BuyFreeSpinsRequest
    );
    this.accelerate = this.createBaseState(GameStateMachineStates.Accelerate);
    this.waitRequest = this.createBaseState(GameStateMachineStates.WaitRequest);
    this.waitaccelerationComplete = this.createBaseState(
      GameStateMachineStates.WaitAccelerationComplete
    );
    this.freeSpinsRecovery = this.createBaseState(GameStateMachineStates.FreeSpinRecovery);
    this.freeSpinsCompletion = this._createSuspendState(GameStateMachineStates.FreeSpinsCompletion);
    this.idle = this._createSuspendState(GameStateMachineStates.Idle);
    this.makeRequest = this.createBaseState(GameStateMachineStates.MakeRequest);
    this.stopping = this.createBaseState(GameStateMachineStates.Stopping);
    this.immediatelyStop = this.createBaseState(GameStateMachineStates.ImmediatelyStop);
    this.stop = this.createBaseState(GameStateMachineStates.Stop);
    this.animation = this.createBaseState(GameStateMachineStates.Animation);
    this.endAnimation = this.createBaseState(GameStateMachineStates.EndAnimation);
    this.shortWinLines = this.createBaseState(GameStateMachineStates.ShortWinLines);
    this.beginFreeSpins = this.createBaseState(GameStateMachineStates.BeginFreeSpins);
    this.beginFreeSpinsPopup = this._createSuspendState(GameStateMachineStates.BeginFreeSpinsPopup);
    this.endFreeSpins = this.createBaseState(GameStateMachineStates.EndOfFreeSpins);
    this.endFreeSpinsPopup = this._createSuspendState(GameStateMachineStates.EndOfFreeSpinsPopup);
    this.reBuyFreeSpinsPopup = this.createBaseState(GameStateMachineStates.ReBuyFreeSpinsPopup);
    this.beginBonus = this.createBaseState(GameStateMachineStates.BeginBonus);
    this.bonus = this._createSuspendState(GameStateMachineStates.Bonus);
    this.endBonus = this._createSuspendState(GameStateMachineStates.EndBonus);
    this.endScatter = this._createSuspendState(GameStateMachineStates.EndScatter);
    this.regularSpins = this._createSuspendState(GameStateMachineStates.RegularSpin);
    this.autoSpins = this.createBaseState(GameStateMachineStates.AutoSpin);
    this.beginRespin = this.createBaseState(GameStateMachineStates.BeginRespin);
    this.respin = this.createBaseState(GameStateMachineStates.Respin);
    this.freeSpins = this.createBaseState(GameStateMachineStates.FreeSpin);
    this.beginScatter = this.createBaseState(GameStateMachineStates.BeginScatter);
    this.bonusRecovery = this.createBaseState(GameStateMachineStates.BonusRecovery);
    this.scatter = this._createSuspendState(GameStateMachineStates.Scatter);
    this._stm.setInitialState(this.init);
  }

  protected createRules(): void {
    throw new Error('Method createRules must be implemented in derived class');
  }

  // This code left here for example purposes, but commented out to avoid confusion.
  // protected createRules(): void {
  //   this.createRule(
  //     this.regularSpins,
  //     this.beginFreeSpins,
  //     (s, r) => new StopToBeginCustomFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.idle,
  //     this.beginFreeSpins,
  //     (s, r) => new StopToBeginCustomFreeSpins<TResponse>(s, r)
  //   );
  //   // this.createRule(this.stop, this.shortWinLines, (s, r) => new StopToShiftingCollapse<TResponse>(s, r));
  //   this.createRule(this.init, this.scatter, (s, r) => new InitToScatter<TResponse>(s, r));
  //   this.createRule(this.init, this.idle, (s, r) => new InitToIdle<TResponse>(s, r));
  //   this.createRule(this.idle, this.spinsRequest, (s, r) => new SpinRule<TResponse>(s, r));
  //   this.createRule(
  //     this.spinsRequest,
  //     this.accelerate,
  //     (s, r) => new BaseGameRule<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.idle,
  //     this.buyFreeSpinsRequest,
  //     (s, r) => new BuyFreeSpinsRule<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.buyFreeSpinsRequest,
  //     this.accelerate,
  //     (s, r) => new BaseGameRule<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.beginRespin,
  //     this.spinsRequest,
  //     (s, r) => new BeginRespinToAccelerate<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.accelerate,
  //     this.waitRequest,
  //     (s, r) => new WaitResponseEventRule<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.waitRequest,
  //     this.waitaccelerationComplete,
  //     (s, r) => new WaitAccelerationCompleteRule<TResponse>(this._container, s, r)
  //   );
  //   this.createRule(
  //     this.accelerate,
  //     this.waitaccelerationComplete,
  //     (s, r) => new AccelerateToWaitAccelerationComplete<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.waitaccelerationComplete,
  //     this.stopping,
  //     (s, r) => new WaitAccelerationCompleteRule<TResponse>(this._container, s, r)
  //   );
  //   this.createRule(this.stopping, this.immediatelyStop, (s, r) => new StopRule<TResponse>(s, r));
  //   this.createRule(this.immediatelyStop, this.stop, (s, r) => new BaseGameRule<TResponse>(s, r));
  //   this.createRule(this.stopping, this.stop, (s, r) => new BaseGameRule<TResponse>(s, r));
  //   this.createRule(this.stop, this.respin, (s, r) => new StopToRespin<TResponse>(s, r));
  //   this.createRule(
  //     this.stop,
  //     this.shortWinLines,
  //     (s, r) => new StopToBeginRespin<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.animation,
  //     this.beginScatter,
  //     (s, r) => new StopToBeginScatter<TResponse>(s, r)
  //   );
  //   this.createRule(this.animation, this.beginBonus, (s, r) => new StopToBonus<TResponse>(s, r));
  //   this.createRule(
  //     this.animation,
  //     this.endFreeSpins,
  //     (s, r) => new StopToEndFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.animation,
  //     this.beginFreeSpins,
  //     (s, r) => new StopToBeginFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.animation,
  //     this.shortWinLines,
  //     (s, r) => new StopToFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(this.stop, this.animation, (s, r) => new BaseGameRule<TResponse>(s, r));
  //   this.createRule(
  //     this.animation,
  //     this.shortWinLines,
  //     (s, r) =>
  //       new StopToAutoSpin<TResponse>(s, r, this._container.resolve<ISpinParams>(T_ISpinParams))
  //   );
  //   this.createRule(
  //     this.shortWinLines,
  //     this.beginRespin,
  //     (s, r) => new StopToBeginRespin<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.shortWinLines,
  //     this.autoSpins,
  //     (s, r) =>
  //       new StopToAutoSpin<TResponse>(s, r, this._container.resolve<ISpinParams>(T_ISpinParams))
  //   );
  //   this.createRule(
  //     this.animation,
  //     this.regularSpins,
  //     (s, r) => new StopToRegularSpin<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.endFreeSpinsPopup,
  //     this.reBuyFreeSpinsPopup,
  //     (s, r) => new ResumeRule<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.reBuyFreeSpinsPopup,
  //     this.beginFreeSpins,
  //     (s, r) => new StopToBeginFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(this.regularSpins, this.spinsRequest, (s, r) => new SpinRule<TResponse>(s, r));
  //   this.createRule(this.autoSpins, this.spinsRequest, (s, r) => new BaseGameRule<TResponse>(s, r));
  //   this.createRule(this.regularSpins, this.bonus, (s, r) => new BonusGameRule<TResponse>(s, r));
  //   this.createRule(this.init, this.bonus, (s, r) => new InitToBonusRecovery<TResponse>(s, r));
  //   this.createRule(
  //     this.init,
  //     this.freeSpinsCompletion,
  //     (s, r) => new InitToFreeSpinsRecovery<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.freeSpinsCompletion,
  //     this.freeSpinsRecovery,
  //     (s, r) => new ResumeRule<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.shortWinLines,
  //     this.freeSpins,
  //     (s, r) => new StopToFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.shortWinLines,
  //     this.regularSpins,
  //     (s, r) =>
  //       new ShortWinLinesToRegularSpins<TResponse>(
  //         s,
  //         r,
  //         this._container.resolve<ISpinParams>(T_ISpinParams)
  //       )
  //   );
  //   this.createRule(this.scatter, this.endScatter, (s, r) => new ResumeRule<TResponse>(s, r));
  //   this.createRule(
  //     this.endScatter,
  //     this.endFreeSpins,
  //     (s, r) => new ScatterToEndFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.endScatter,
  //     this.beginBonus,
  //     (s, r) => new ScatterToBonus<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.endScatter,
  //     this.freeSpins,
  //     (s, r) => new ScatterToFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.endScatter,
  //     this.autoSpins,
  //     (s, r) =>
  //       new StopToAutoSpin<TResponse>(s, r, this._container.resolve<ISpinParams>(T_ISpinParams))
  //   );
  //   this.createRule(
  //     this.endScatter,
  //     this.regularSpins,
  //     (s, r) => new ScatterToRegularSpins<TResponse>(s, r)
  //   );
  //   this.createRule(this.bonus, this.endBonus, (s, r) => new ResumeRule<TResponse>(s, r));
  //   this.createRule(
  //     this.endBonus,
  //     this.beginScatter,
  //     (s, r) => new BonusToBeginScater<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.endBonus,
  //     this.beginFreeSpins,
  //     (s, r) => new BonusToBeginFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.endBonus,
  //     this.endFreeSpins,
  //     (s, r) => new BonusToEndFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(this.endBonus, this.freeSpins, (s, r) => new BonusToFreeSpins<TResponse>(s, r));
  //   this.createRule(
  //     this.endBonus,
  //     this.autoSpins,
  //     (s, r) =>
  //       new StopToAutoSpin<TResponse>(s, r, this._container.resolve<ISpinParams>(T_ISpinParams))
  //   );
  //   this.createRule(this.endBonus, this.regularSpins, (s, r) => new ResumeRule<TResponse>(s, r));
  //   this.createRule(this.freeSpins, this.spinsRequest, (s, r) => new SpinRule<TResponse>(s, r));
  //   this.createRule(
  //     this.endFreeSpins,
  //     this.endFreeSpinsPopup,
  //     (s, r) => new EndFreeSpinsPopupPopupRule<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.endFreeSpins,
  //     this.regularSpins,
  //     (s, r) => new EndFreeSpinsToRegularSpins<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.beginFreeSpins,
  //     this.beginFreeSpinsPopup,
  //     (s, r) => new BaseGameRule<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.beginFreeSpinsPopup,
  //     this.freeSpins,
  //     (s, r) => new SpinRule<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.freeSpinsRecovery,
  //     this.spinsRequest,
  //     (s, r) => new BaseGameRule<TResponse>(s, r)
  //   );
  //   this.createRule(this.idle, this.bonus, (s, r) => new BonusGameRule<TResponse>(s, r));
  //   this.createRule(this.beginBonus, this.bonus, (s, r) => new BaseGameRule<TResponse>(s, r));
  //   this.createRule(
  //     this.reBuyFreeSpinsPopup,
  //     this.autoSpins,
  //     (s, r) =>
  //       new StopToAutoSpin<TResponse>(s, r, this._container.resolve<ISpinParams>(T_ISpinParams))
  //   );
  //   this.createRule(
  //     this.reBuyFreeSpinsPopup,
  //     this.regularSpins,
  //     (s, r) => new ResumeTransition<TResponse>(s, r)
  //   );
  //   this.createRule(
  //     this.reBuyFreeSpinsPopup,
  //     this.beginFreeSpins,
  //     (s, r) => new StopToBeginFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(this.beginScatter, this.scatter, (s, r) => new BaseGameRule<TResponse>(s, r));
  //   this.createRule(
  //     this.respin,
  //     this.beginRespin,
  //     (s, r) => new RespinToBeginRespin<TResponse>(s, r)
  //   );
  //   this.createRule(this.respin, this.animation, (s, r) => new StopToBonus<TResponse>(s, r));
  //   this.createRule(this.respin, this.animation, (s, r) => new StopToBeginScatter<TResponse>(s, r));
  //   this.createRule(
  //     this.respin,
  //     this.animation,
  //     (s, r) => new StopToBeginFreeSpins<TResponse>(s, r)
  //   );
  //   this.createRule(this.respin, this.animation, (s, r) => new StopToFreeSpins<TResponse>(s, r));
  //   this.createRule(this.respin, this.animation, (s, r) => new StopToEndFreeSpins<TResponse>(s, r));
  //   this.createRule(this.respin, this.animation, (s, r) => new StopToRegularSpin<TResponse>(s, r));
  // }

  public isStateActive(id: string): boolean {
    return this._stm.isActive(id);
  }

  public doStart(response: TResponse): void {
    this._responseHolder.curResponse = response;
    this._stm.start();
  }

  public doSpin(): void {
    this._stm.dispatchEvent(new SpinEvent());
  }

  public buyFreeSpins(): void {
    this._stm.dispatchEvent(new BuyFreeSpinsEvent());
  }

  public doStop(): void {
    this._stm.dispatchEvent(new StopEvent());
  }

  public toBonus(): void {
    this._stm.dispatchEvent(new BonusGameEvent());
  }

  public resume(): void {
    ContextMarshaller.marshalAsync(() => this._stm.dispatchEvent(new ResumeEvent()));
  }

  public update(dt: number): void {
    this._stm.update(dt);
  }

  public interrupt(): void {
    this._stm.stop();
  }

  public get isAutoSpins(): boolean {
    return this._spinParams ? this._spinParams.autoSpin : false;
  }

  public findById(id: string): BaseGameState<TResponse> | null {
    return this._stm.findById(id) as BaseGameState<TResponse>;
  }

  public isActive(stateId: string): boolean {
    return this._stm.isActive(stateId);
  }

  protected createRule(
    from: BaseGameState<TResponse>,
    to: BaseGameState<TResponse>,
    factory: RuleFactory<TResponse>
  ): BaseGameRule<TResponse> {
    const rule = factory(from, this._responseHolder);
    rule.targetState = to;
    this._stm.addRule(from, to, rule);
    return rule;
  }

  protected appendRule(
    from: BaseGameState<TResponse>,
    to: BaseGameState<TResponse>,
    factory: RuleFactory<TResponse>
  ): BaseGameRule<TResponse> {
    const rule = factory(from, this._responseHolder);
    rule.targetState = to;
    this._stm.appendRule(from, to, rule);
    return rule;
  }

  protected createBaseState(stateId: string): BaseGameState<TResponse> {
    const result = new BaseGameState<TResponse>(this._responseHolder, stateId);
    this._stm.addState(result);
    return result;
  }

  private _createSuspendState(stateId: string): SuspendState<TResponse> {
    const result = new SuspendState<TResponse>(this._responseHolder, stateId);
    this._stm.addState(result);
    return result;
  }

  private createSpinState(stateId: string): RequestState<TResponse> {
    const result = new SpinRequestState<TResponse>(this.container, this._responseHolder, stateId);
    this._stm.addState(result);
    return result;
  }

  private createSlotMachineStoreState(stateId: string): BaseGameState<TResponse> {
    const result = new SlotMachineStoreState<TResponse>(
      this.container,
      this._responseHolder,
      stateId
    );
    this._stm.addState(result);
    return result;
  }
}
